import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  Tabs,
  Tab,
  Box,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Button,
  Divider,
  alpha,
  Paper,
  Stack,
  Chip,
  DialogTitle,
  DialogActions,
} from '@mui/material';
import {
  Close as CloseIcon,
  ArrowBack as ArrowBackIcon,
  LocalOffer as TagIcon,
  PersonOutline,
  ContactPhoneOutlined,
  EmailOutlined,
  CategoryOutlined,
  AdminPanelSettingsOutlined,
  InfoOutlined,
} from '@mui/icons-material';

import { getStates, getZones, getLga, getUser } from '../../../../helpers/api';
import ModalHeader from './ModalHeader'; // Assuming this is also converted/compatible
import UpdateUserForm from './UpdateUserForm'; // Assuming this handles the edit logic
import './UpdateUser.css';

const UpdateUser = ({ open, close, name, userCategory, email, areaPermit, userId }) => {
  const [zones, setZones] = useState([]);
  const [states, setStates] = useState([]);
  const [lga, setLga] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(false);
  const [tabValue, setTabValue] = useState(0);

  const canUpdateConsultant = userCategory === 'CONSULTANT';
  const canUpdateEdm = userCategory === 'EDM';
  const canUpdateRegulator = userCategory === 'REGULATOR';

  useEffect(() => {
    refreshZones();
    refreshStates();
    refreshLGA();
    refreshUser();
  }, []);

  useEffect(() => {
    if (open) refreshUser();
  }, [reload, open]);

  const refreshZones = () => {
    getZones()
      .then((res) => setZones(res.result.data))
      .catch(console.error);
  };

  const refreshStates = () => {
    getStates()
      .then((res) => setStates(res.result.data))
      .catch(console.error);
  };

  const refreshLGA = () => {
    getLga()
      .then((res) => setLga(res.result.data))
      .catch(console.error);
  };

  const refreshUser = () => {
    setLoading(true);
    getUser(userId)
      .then((res) => {
        if (res) {
          setData(res.result.data);
          setReload(false);
        }
      })
      .finally(() => setLoading(false));
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const InfoItem = ({ label, value, icon }) => (
    <Grid item xs={12} sm={6} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            fontWeight={700}
            sx={{ display: 'block', textTransform: 'uppercase' }}
          >
            {label}
          </Typography>
          <Typography variant="body2" fontWeight={600} color="text.primary">
            {value || 'N/A'}
          </Typography>
        </Box>
      </Stack>
    </Grid>
  );

  const displayUserData = () => {
    if (loading || !data) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
          <CircularProgress size={30} />
        </Box>
      );
    }

    const { zonePermission = [], statePermission = [], lgaPermission = [] } =
      data.userAreaPermissions || {};

    return (
      <Grid container spacing={2}>
        <InfoItem
          label="First Name"
          value={data.firstname}
          icon={<PersonOutline fontSize="small" />}
        />
        <InfoItem
          label="Last Name"
          value={data.lastname}
          icon={<PersonOutline fontSize="small" />}
        />
        <InfoItem
          label="Username"
          value={data.username}
          icon={<AdminPanelSettingsOutlined fontSize="small" />}
        />
        <InfoItem
          label="Email Address"
          value={data.email}
          icon={<EmailOutlined fontSize="small" />}
        />
        <InfoItem
          label="Phone Number"
          value={data.phone}
          icon={<ContactPhoneOutlined fontSize="small" />}
        />
        <InfoItem label="Status" value={data.status} icon={<InfoOutlined fontSize="small" />} />
        <InfoItem
          label="Category"
          value={data.userCategory}
          icon={<CategoryOutlined fontSize="small" />}
        />
        <InfoItem
          label="Role"
          value={data.role}
          icon={<AdminPanelSettingsOutlined fontSize="small" />}
        />

        {(zonePermission.length > 0 || statePermission.length > 0 || lgaPermission.length > 0) && (
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight={700} gutterBottom color="primary">
              Assigned Areas
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[...zonePermission, ...statePermission, ...lgaPermission].map((area, idx) => (
                <Chip
                  key={idx}
                  icon={<TagIcon style={{ fontSize: '14px' }} />}
                  label={area.zoneName || area.stateName || area.lgaName}
                  size="small"
                  variant="outlined"
                  sx={{
                    borderRadius: '6px',
                    fontWeight: 600,
                    bgcolor: alpha('#0c5bab', 0.05),
                    borderColor: alpha('#0c5bab', 0.2),
                  }}
                />
              ))}
            </Box>
          </Grid>
        )}
      </Grid>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: { borderRadius: '16px', overflow: 'hidden' } }}
    >
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: '#fff',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" color="text.secondary" fontWeight={700}>
          User Profile
        </Typography>
        <IconButton onClick={close} size="small">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 0, display: 'flex', minHeight: '450px' }}>
        {/* VERTICAL TABS SIDEBAR */}
        <Box
          sx={{
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: alpha('#F4F6F8', 0.4),
            width: '300px',
          }}
        >
          <Tabs
            orientation="vertical"
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                alignItems: 'flex-start',
                textAlign: 'left',
                fontWeight: 700,
                fontSize: '0.85rem',
                py: 2,
                '&.Mui-selected': { color: 'primary.main', bgcolor: alpha('#0c5bab', 0.05) },
              },
              '& .MuiTabs-indicator': { left: 0, right: 'auto', width: 3 },
            }}
          >
            <Tab label="Basic Info" />
            {canUpdateRegulator && <Tab label="Additional Info" />}
          </Tabs>
        </Box>

        {/* TAB PANELS */}
        <Box sx={{ flexGrow: 1, p: 4, bgcolor: '#fff' }}>
          {tabValue === 0 && displayUserData()}
          {tabValue === 1 && canUpdateRegulator && (
            <UpdateUserForm
              name={name}
              email={email}
              category={userCategory}
              permit={areaPermit}
              zoneList={zones}
              stateList={states}
              userId={userId}
              lgaList={lga}
              userData={data}
              closeUpdateModal={close}
              reload={(val) => setReload(val)}
            />
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={close}
          variant="outlined"
          sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 700 }}
        >
          Back
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateUser;
